import registerService from '../../../services/registerService';
import { defaultKeyFieldFor } from '../../../modules/registerLinkOptions';
import { log, warn, error } from '@/utils/debugLogger';

/**
 * Register column link designations for ExcelUpload — moved here verbatim from ExcelUpload.vue
 * (AVA-602 slice 1b.2) so the wizard component stays within the file-size budget. Behaviour is
 * unchanged; the only addition is `importPhase` updates in applyImportColumnLinks so the Complete
 * step can show which phase is running.
 *
 * Host requirements: `register`, `columnMappings`, `importPhase` (data), `$refs.fieldMapping`,
 * `$dialog.notify`.
 */
export default {
  methods: {
    /**
     * Persist the column link designations the mapping step reported as changed.
     *
     * Each one is a `PUT api/registers/{id}/columns/{col}/link` — the existing per-column
     * endpoint; the schema-update payload has no channel for a designation. `columnName` is the
     * column's INTERNAL name, which is what that endpoint matches on.
     *
     * A failure here does NOT undo the schema update that already committed: the designations are
     * independent metadata, so we report which columns did not save and leave the rest applied.
     *
     * Returns BOTH halves of the outcome — the callers need `saved` to decide whether anything
     * reached the server (a re-resolve is only owed when a designation actually changed), and
     * `failed` carries the names already surfaced to the user.
     * @param {?Array<object>} changes - designations to write; defaults to the mapping payload
     * @returns {Promise<{saved: Array<object>, failed: Array<string>}>}
     */
    async persistColumnLinks(changes = null) {
      const pending = changes || (this.columnMappings && this.columnMappings.columnLinks) || [];
      if (!pending.length) return { saved: [], failed: [] };

      log('Persisting register column link designations:', pending);

      const saved = [];
      const failed = [];
      for (const change of pending) {
        try {
          await registerService.updateColumnLink(this.register.id, change.columnName, change);
          saved.push(change);
          log('Column link saved', { column: change.columnName, type: change.linkEntityType });
        } catch (err) {
          failed.push(change.fieldKey || change.columnName);
          error('Failed to save column link designation:', change.columnName, err);
        }
      }

      if (failed.length) {
        warn('Some column link designations were not saved:', failed);
        this.$dialog.notify.error(`Link designation not saved for: ${failed.join(', ')}`);
      }

      return { saved, failed };
    },

    /**
     * Persist the designations chosen during an import, then have the server resolve them.
     *
     * WHY AFTER THE IMPORT, NOT BEFORE IT: the link endpoint identifies a column by its INTERNAL
     * name, and on a first import that name does not exist until the server builds the schema —
     * which it does at the very END of the import call (`RegisterDataService.Import.cs`:
     * rows saved -> tag linking -> POI resolver -> UpdateRegisterSchemaAsync), all inside one
     * POST. There is therefore no moment in the existing sequence between "schema exists" and
     * "rows imported" for the client to write into, so the designations are written afterwards
     * and the register is re-resolved (the documented fallback).
     *
     * The internal names are NOT guessed and the mapping order is NOT assumed: we re-read the
     * created schema and match on the column identities the server actually recorded (see
     * matchDesignationsToColumns). A designation we cannot match unambiguously is SKIPPED and
     * reported — designating the wrong column silently is the failure mode this guards against.
     */
    async applyImportColumnLinks() {
      const designations = (this.columnMappings && this.columnMappings.columnLinks) || [];
      if (!designations.length) return;

      let columns = null;
      try {
        const schema = await registerService.getRegisterSchema(this.register.id);
        columns = Array.isArray(schema?.columns) ? schema.columns : null;
      } catch (err) {
        error('Failed to read the imported schema; link designations were not saved:', err);
      }

      if (!columns || !columns.length) {
        // Without the created schema we cannot learn the internal column names, and guessing
        // them would designate the wrong column — so save nothing and say so.
        warn('No usable schema for the imported register; link designations were not saved.', {
          registerId: this.register?.id,
          designations: designations.map((entry) => entry.fieldKey),
        });
        this.$dialog.notify.error('Link designations were not saved: could not read the register schema.');
        return;
      }

      const { matched, unmatched } = this.matchDesignationsToColumns(designations, columns);

      if (unmatched.length) {
        warn('Could not match these designated columns in the imported schema:', unmatched);
        this.$dialog.notify.error(`Link designation not saved for: ${unmatched.join(', ')}`);
      }
      if (!matched.length) return;

      this.importPhase = 'links';
      const { saved } = await this.persistColumnLinks(matched);
      if (saved.length > 0) {
        // Every designation here is new to the register, so they are all resolution inputs —
        // no need for the schema path's "did this actually change what resolves?" test.
        this.importPhase = 'resolving';
        await this.resolveRegisterLinks();
      }
    },

    /**
     * Pair each import designation with the internal column name the server assigned it.
     *
     * Two candidates, each compared against its OWN field only — never fieldKey-vs-displayName,
     * which could collide with an unrelated column and designate it silently:
     *   1. `originalName` === the source column. What the server records for a column it has
     *      just created, so this is the first-import answer.
     *   2. `displayName` === the field name the user assigned. Needed on a re-import, where
     *      `Register.UpdateSchema` merges by internal name and KEEPS the stored originalName
     *      (which may name a different source column than this import used).
     * No positional fallback: an unmatched designation is reported, not guessed.
     * @param {Array<object>} designations - entries from FieldMapping (fieldKey + displayName)
     * @param {Array<object>} columns - RegisterColumnVM entries from the created schema
     * @returns {{matched: Array<object>, unmatched: Array<string>}}
     */
    matchDesignationsToColumns(designations, columns) {
      const byOriginalName = this.indexColumnsBy(columns, 'originalName');
      const byDisplayName = this.indexColumnsBy(columns, 'displayName');

      const matched = [];
      const unmatched = [];
      designations.forEach((designation) => {
        const { fieldKey, displayName } = designation;
        const columnName =
          byOriginalName.get(fieldKey) ||
          (displayName ? byDisplayName.get(displayName) : null) ||
          null;
        if (!columnName) {
          unmatched.push(fieldKey);
          return;
        }
        matched.push({ ...designation, columnName });
      });

      log('Matched import link designations to internal column names', {
        registerId: this.register?.id,
        matched: matched.map((entry) => ({
          fieldKey: entry.fieldKey,
          columnName: entry.columnName,
          type: entry.linkEntityType,
        })),
        unmatched,
      });

      return { matched, unmatched };
    },

    /**
     * Index the schema's non-system columns by one field -> internal column name, EXCLUDING any
     * value carried by more than one column. An ambiguous key must resolve to nothing: leaving a
     * designation unsaved is recoverable, designating the wrong column is not.
     * @param {Array<object>} columns - RegisterColumnVM entries
     * @param {string} field - 'originalName' | 'displayName'
     * @returns {Map<string, string>}
     */
    indexColumnsBy(columns, field) {
      const index = new Map();
      const ambiguous = new Set();

      columns.forEach((col) => {
        if (!col || col.isSystemColumn || !col.name) return;
        const key = col[field];
        if (!key) return;
        if (index.has(key)) {
          ambiguous.add(key);
          return;
        }
        index.set(key, col.name);
      });

      ambiguous.forEach((key) => index.delete(key));
      if (ambiguous.size) {
        warn(`Ambiguous ${field} in the register schema; those columns cannot be designated:`, [
          ...ambiguous,
        ]);
      }
      return index;
    },

    /**
     * Ask the server to re-resolve the register against its current designations. FAIL-SOFT: the
     * work that got us here (an import, or a schema update) is already committed, and the read
     * path falls back to live resolution while a register is not marked complete — so a failure
     * here is reported, never rethrown.
     *
     * Reports the outcome rather than acting on it: the import path stays silent (it already
     * has its own result screen), while the schema dialog turns a failure into a notification.
     * @returns {Promise<boolean>} true when the server accepted the re-resolve
     */
    async resolveRegisterLinks() {
      try {
        const summary = await registerService.resolveLinks(this.register.id);
        log('Register links re-resolved', { registerId: this.register?.id, summary });
        return true;
      } catch (err) {
        warn('Could not re-resolve register links; the register stays on live resolution until the next import:', err);
        return false;
      }
    },

    /**
     * Re-resolve the register after a schema-dialog save, but only when the save actually changed
     * what the server resolves — and tell the user either way.
     *
     * AWAITED ON PURPOSE (not fire-and-forget): the mapping step is still mounted here, so its
     * own busy state covers the wait, and the notification below cannot land on a dialog that the
     * `complete` emit has already torn down.
     * @param {Array<object>} saved - the designations persistColumnLinks actually wrote
     * @param {?object} baseline - snapshotLinkDesignations() taken before the save
     * @param {?object} savedFieldNames - the schema payload's fieldMappings (fieldKey -> new
     *   display name), i.e. the names this very save wrote
     */
    async reResolveAfterDesignationChange(saved, baseline, savedFieldNames) {
      if (!this.hasResolutionInputChange(saved, baseline, savedFieldNames)) {
        log('Schema saved with no resolution-affecting change; links left as they are.', {
          registerId: this.register?.id,
          savedDesignations: (saved || []).map((change) => change.columnName),
        });
        return;
      }

      log('Resolution inputs changed; re-resolving the register links.', {
        registerId: this.register?.id,
        savedDesignations: (saved || []).map((change) => ({
          column: change.columnName,
          type: change.linkEntityType,
        })),
        renamedDesignatedColumns: this.renamedDesignatedColumns(baseline, savedFieldNames),
      });

      this.$dialog.notify.info('Re-resolving register links…');
      const resolved = await this.resolveRegisterLinks();
      if (!resolved) {
        this.$dialog.notify.warning(
          'Schema saved, but the register links were not re-resolved. Dashboards fall back to live resolution until the next import.',
        );
      }
    },

    /**
     * The designations as they stood BEFORE this save — the mapping step's OWN baseline, keyed
     * by `fieldKey` exactly like the change list it produced.
     *
     * Deliberately not the `register` prop: that arrives from the register LIST and can be older
     * than the schema the mapping step actually diffed against, and a stale baseline fails in the
     * dangerous direction — it agrees with a change that really did alter what resolves, so no
     * re-resolve fires and the register is left with nothing to rebuild it. The mapping step
     * fetches its baseline when the dialog opens, so comparing against it is the exact inverse of
     * the diff that produced the changes.
     *
     * Captured before the save because the mapping step is unmounted as soon as `step` advances;
     * holding the reference is enough, since a re-hydrate assigns a fresh object rather than
     * mutating this one.
     * @returns {?object} fieldKey -> {linkEntityType, linkKeyField, ...}, null when unavailable
     */
    snapshotLinkDesignations() {
      const baseline = this.$refs.fieldMapping?.columnLinksBaseline;
      return baseline && typeof baseline === 'object' ? baseline : null;
    },

    /**
     * True when this save changed something the SERVER'S resolver reads.
     *
     * The server drops a register's stored resolution only when its designation hash changes,
     * and that hash covers, per designated column, the PHYSICAL COLUMN NAME (the display name),
     * the entity type and the effective key field — NOT `isDefaultHighlight` /
     * `isDefaultContext`, which are widget-level roles the client derives at paint time. So a
     * "Register default" toggle leaves every stored link valid, and re-resolving the whole
     * register for it would be pure waste — while RENAMING a designated column invalidates the
     * resolution without touching a designation at all (see renamedDesignatedColumns).
     *
     * Deliberately biased TOWARDS firing: a needless re-resolve costs time, while a missed one
     * leaves the register with no valid resolution and nothing to rebuild it. A cleared
     * designation, a column we hold no baseline for, and a missing baseline altogether therefore
     * all count as changed.
     * @param {Array<object>} saved - the designations persistColumnLinks actually wrote
     * @param {?object} baseline - snapshotLinkDesignations() taken before the save
     * @param {?object} savedFieldNames - fieldKey -> display name, as sent to the server
     * @returns {boolean}
     */
    hasResolutionInputChange(saved, baseline, savedFieldNames) {
      const written = saved || [];

      // A rename produces no designation change at all, so it has to be tested separately —
      // this is the half that the `saved` loop below structurally cannot see.
      if (this.renamedDesignatedColumns(baseline, savedFieldNames).length > 0) return true;

      // Nothing to compare against — assume the save mattered rather than risk stranding the
      // register on the fallback.
      if (!baseline) return written.length > 0;

      // An absent key field means "the type's default", which is how the server normalises it
      // before hashing — comparing the raw value would flag a change that isn't one.
      const effectiveKeyField = (link) =>
        link.linkKeyField || defaultKeyFieldFor(link.linkEntityType);

      return written.some((change) => {
        // A cleared designation always invalidates: its stored links are now orphaned.
        if (!change.linkEntityType) return true;

        const before = baseline[change.fieldKey];
        if (!before) return true;

        return (
          before.linkEntityType !== change.linkEntityType ||
          effectiveKeyField(before) !== effectiveKeyField(change)
        );
      });
    },

    /**
     * The DESIGNATED columns whose display name this save changed.
     *
     * WHY A RENAME IS A RESOLUTION CHANGE: the server identifies a link's source column by its
     * PHYSICAL name — the display name, the SQL-view alias — and hashes that name into the
     * register's resolution config. Rename a designated column and the stored hash no longer
     * matches the schema, so the read gate closes and dashboards drop to live resolution. No
     * designation was touched, so nothing invalidates and nothing rebuilds: without this test
     * the register stays on the fallback permanently. Re-resolving rewrites the links under the
     * new column name and stores the new hash, which reopens the gate.
     *
     * Renaming an UNDESIGNATED column is genuinely nothing: such a column is not part of the
     * hash at all, so it never appears here and no re-resolve fires.
     *
     * Compared against the SAME baseline the designation diff uses, so there is one answer to
     * "what did this save change". A designated column the payload names differently — or does
     * not name at all — counts as changed: the fallback direction is a wasted re-resolve, never
     * a stranded register.
     * @param {?object} baseline - snapshotLinkDesignations() taken before the save
     * @param {?object} savedFieldNames - fieldKey -> display name, as sent to the server
     * @returns {Array<string>} fieldKeys of the renamed designated columns
     */
    renamedDesignatedColumns(baseline, savedFieldNames) {
      if (!baseline) return [];

      const names = savedFieldNames || {};
      // A payload that names nothing renames nothing: the server writes only the names it is
      // given, so there is no drift to be defensive about here.
      if (Object.keys(names).length === 0) return [];

      return Object.keys(baseline).filter((fieldKey) => {
        const before = baseline[fieldKey];
        // Undesignated columns are absent from the server's hash — a rename cannot invalidate
        // a resolution that never referenced them.
        if (!before || !before.linkEntityType) return false;

        // Both sides fall back to the internal column name when a column carries no display
        // name, matching how the server picks the physical column.
        return (names[fieldKey] || null) !== (before.storedDisplayName || null);
      });
    },
  },
};
